import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import { createCheckoutSession, createPortalSession, fetchPricing } from '../../services/stripeService';
import { getProfile, saveProfile, DEFAULT_EMAIL_PREFS, withEmailPrefDefaults, uploadBusinessLogo } from '../../services/profileService';
import { GEAR_CATEGORIES, MAX_GEAR, cleanGear } from '../../utils/gear';
import { ProfileCalendar } from './ProfileCalendar';
import { countFollowers, countFollowing } from '../../services/followService';
import { LINK_TYPES } from '../common/ProfileLinks';
import { UserProfilePanel } from './UserProfilePanel';
import { LegalModal } from '../Legal/LegalModal';
import { Modal } from '../common/Modal';
import { BusinessApplicationModal } from './BusinessApplicationModal';
import { OrganizerPayouts } from './OrganizerPayouts';
import { syncBusinessIdentity } from '../../services/businessIdentityService';
import { useOrganizerPayouts } from '../../hooks/useOrganizerPayouts';
import './ProfilePage.css';

function CheckIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <polyline points="20 6 9 17 4 12"/>
    </svg>
  );
}

export function ProfilePage({ onClose }) {
  const { user, profile, isSubscribed, claims, subscription, refreshSubscription, linkGoogle } = useAuth();

  const { connectStatus, connectLoading, connectError, handleConnectStripe } =
    useOrganizerPayouts(user, { onConnected: () => showToast('Stripe account connected!') });
  const [portalLoading,       setPortalLoading]        = useState(false);
  const [syncLoading,         setSyncLoading]          = useState(false);
  const [toast,               setToast]                = useState('');
  const [googleLinked,        setGoogleLinked]         = useState(() => user.providerData.some(p => p.providerId === 'google.com'));
  const [linkLoading,         setLinkLoading]          = useState(false);
  const [legalTab,            setLegalTab]             = useState(null);
  const [selectedPlan,        setSelectedPlan]         = useState('yearly');
  const [checkoutLoading,     setCheckoutLoading]      = useState(false);
  // Live from Stripe — keeps today's real prices as a fallback if the fetch fails.
  const [pricing, setPricing] = useState({
    monthly: { amount: 1.99, currency: 'AUD' },
    yearly:  { amount: 9.99, currency: 'AUD' },
  });
  useEffect(() => {
    fetchPricing()
      .then(data => setPricing(prev => ({ ...prev, ...data })))
      .catch(() => {});
  }, []);

  // Public profile editor
  const [bio,           setBio]           = useState('');
  const [isBusiness,    setIsBusiness]    = useState(false);
  const [businessName,  setBusinessName]  = useState('');
  const [businessEmail, setBusinessEmail] = useState('');
  const [businessPhone, setBusinessPhone] = useState('');

  // Business application (ABN + public liability proof + services/socials) —
  // admin-reviewed; approval unlocks the ability to create tours/lessons and
  // connect Stripe (see firestore.rules tours/{tourId}.allow create).
  const [businessWebsite,          setBusinessWebsite]          = useState('');
  const [businessLinks,            setBusinessLinks]            = useState([]); // [{ type, url }]
  const [businessServices,         setBusinessServices]         = useState([]); // ['tours','lessons']
  const [businessABN,              setBusinessABN]              = useState('');
  const [businessInsuranceUrl,     setBusinessInsuranceUrl]     = useState('');
  const [businessInsuranceFileName, setBusinessInsuranceFileName] = useState('');
  const [businessVerifiedStatus,   setBusinessVerifiedStatus]   = useState('unverified');
  const [showBusinessVerifyModal,  setShowBusinessVerifyModal]  = useState(false);
  const [businessLogoUrl,          setBusinessLogoUrl]          = useState('');
  const [logoUploading,            setLogoUploading]            = useState(false);
  const [useBusinessIdentity,      setUseBusinessIdentity]      = useState(false);
  const [links,         setLinks]         = useState([]);   // [{ type, url }]
  const [gear,          setGear]          = useState([]);   // [{ category, name, brand, url }]
  const [profileSaving, setProfileSaving] = useState(false);
  const [followerCount,  setFollowerCount]  = useState(null);
  const [followingCount, setFollowingCount] = useState(null);
  const [showPublic,     setShowPublic]     = useState(false);
  const [emailPrefs,     setEmailPrefs]     = useState(DEFAULT_EMAIL_PREFS);
  const [licenseStage,   setLicenseStage]   = useState(''); // '' | 'learner' | 'provisional' | 'full'

  useEffect(() => {
    if (!user) return;
    countFollowers(user.uid).then(setFollowerCount).catch(() => {});
    countFollowing(user.uid).then(setFollowingCount).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (!user) return;
    getProfile(user.uid).then(p => {
      if (!p) return;
      setBio(p.bio || '');
      setIsBusiness(!!p.isBusiness);
      setBusinessName(p.businessName || '');
      setBusinessEmail(p.businessEmail || '');
      setBusinessPhone(p.businessPhone || '');
      setBusinessWebsite(p.businessWebsite || '');
      setBusinessLinks(Array.isArray(p.businessLinks) ? p.businessLinks : []);
      setBusinessServices(Array.isArray(p.businessServices) ? p.businessServices : []);
      setBusinessABN(p.businessABN || '');
      setBusinessInsuranceUrl(p.businessInsuranceUrl || '');
      setBusinessInsuranceFileName(p.businessInsuranceFileName || '');
      setBusinessVerifiedStatus(p.businessVerifiedStatus || 'unverified');
      setBusinessLogoUrl(p.businessLogoUrl || '');
      setUseBusinessIdentity(!!p.useBusinessIdentity);
      setLinks(Array.isArray(p.links) ? p.links : []);
      setGear(Array.isArray(p.gear) ? p.gear : []);
      setEmailPrefs(withEmailPrefDefaults(p.emailPrefs));
      setLicenseStage(p.licenseStage || '');
    }).catch(() => {});
  }, [user]);

  // Email prefs save immediately on toggle (no Save button needed).
  async function toggleEmailPref(key) {
    const next = { ...emailPrefs, [key]: !emailPrefs[key] };
    setEmailPrefs(next);
    try {
      await saveProfile(user.uid, { emailPrefs: next });
    } catch {
      setEmailPrefs(emailPrefs); // revert on failure
      showToast('Could not save notification setting');
    }
  }

  async function handleLogoChange(e) {
    const file = e.target.files?.[0];
    if (!file || !user) return;
    setLogoUploading(true);
    try {
      const url = await uploadBusinessLogo(user.uid, file);
      setBusinessLogoUrl(url);
      await saveProfile(user.uid, { businessLogoUrl: url });
      if (useBusinessIdentity) await syncBusinessIdentity(user, bizIdentityProfile({ businessLogoUrl: url }));
      showToast('Logo updated');
    } catch (err) {
      showToast(err.message || 'Could not upload logo');
    } finally {
      setLogoUploading(false);
    }
  }

  // The fields syncBusinessIdentity reads to build a listing's business card,
  // from current form state (with any just-changed values layered on top).
  function bizIdentityProfile(overrides = {}) {
    return {
      useBusinessIdentity, businessVerifiedStatus, businessName, businessLogoUrl, businessWebsite,
      businessEmail, businessPhone, businessLinks, businessServices,
      ...overrides,
    };
  }

  // "Use business details for public profile" saves immediately on toggle, then
  // pushes the change to every tour/route already published so Community
  // updates right away (new listings pick it up at creation via the same
  // snapshot — see businessIdentityService).
  async function toggleUseBusinessIdentity() {
    const next = !useBusinessIdentity;
    setUseBusinessIdentity(next);
    try {
      await saveProfile(user.uid, { useBusinessIdentity: next });
      await syncBusinessIdentity(user, bizIdentityProfile({ useBusinessIdentity: next }));
      showToast(next ? 'Now showing your business on your listings' : 'Now showing your personal profile');
    } catch {
      setUseBusinessIdentity(!next);
      showToast('Could not save setting');
    }
  }

  function updateLink(i, patch) {
    setLinks(ls => ls.map((l, j) => j === i ? { ...l, ...patch } : l));
  }
  function addLink() {
    setLinks(ls => [...ls, { type: 'website', url: '' }]);
  }
  function removeLink(i) {
    setLinks(ls => ls.filter((_, j) => j !== i));
  }

  function updateGear(i, patch) {
    setGear(gs => gs.map((g, j) => j === i ? { ...g, ...patch } : g));
  }
  function addGear() {
    setGear(gs => [...gs, { category: 'bike', name: '', brand: '', url: '' }]);
  }
  function removeGear(i) {
    setGear(gs => gs.filter((_, j) => j !== i));
  }

  async function handleSaveProfile() {
    if (!user) return;
    setProfileSaving(true);
    try {
      await saveProfile(user.uid, {
        displayName: user.displayName || null,
        photoURL: user.photoURL || null,
        bio: bio.trim(),
        isBusiness,
        businessName: isBusiness ? businessName.trim() : '',
        businessEmail: isBusiness ? businessEmail.trim() : '',
        businessPhone: isBusiness ? businessPhone.trim() : '',
        links: links.filter(l => l.url.trim()).map(l => ({ type: l.type, url: l.url.trim() })),
        gear: cleanGear(gear),
        licenseStage: licenseStage || null,
      });
      showToast('Profile saved');
    } catch (err) {
      showToast(err.message || 'Could not save profile');
    } finally {
      setProfileSaving(false);
    }
  }

  const subStatus = claims?.subscriptionStatus || subscription?.subscriptionStatus;
  const plan      = claims?.subscriptionPlan   || subscription?.subscriptionPlan;

  function showToast(msg) {
    setToast(msg);
    setTimeout(() => setToast(''), 3000);
  }

  // Close on Escape
  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function handleLinkGoogle() {
    setLinkLoading(true);
    try {
      await linkGoogle();
      setGoogleLinked(true);
      showToast('Google account linked — you can now sign in with Google.');
    } catch (err) {
      showToast(err.message);
    } finally {
      setLinkLoading(false);
    }
  }

  async function handleManageBilling() {
    setPortalLoading(true);
    try {
      const url = await createPortalSession();
      window.location.href = url;
    } catch (err) {
      showToast(err.message);
      setPortalLoading(false);
    }
  }

  async function handleUpgrade() {
    setCheckoutLoading(true);
    try {
      const url = await createCheckoutSession(selectedPlan);
      window.location.href = url;
    } catch (err) {
      showToast(err.message);
      setCheckoutLoading(false);
    }
  }

  async function handleSyncSubscription() {
    setSyncLoading(true);
    try {
      await refreshSubscription();
      showToast('Subscription refreshed');
    } catch (err) {
      showToast(err.message);
    } finally {
      setSyncLoading(false);
    }
  }

  if (!user) return null;

  const initials = user.displayName
    ? user.displayName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()
    : (user.email?.[0] ?? '?').toUpperCase();

  const panel = createPortal(
    <div className="pp-overlay" onClick={e => e.target.classList.contains('pp-overlay') && onClose()}>
      <aside className="pp-panel">

        {/* Header */}
        <div className="pp__header">
          <h2 className="pp__title">Profile & Settings</h2>
          <button className="pp__close" onClick={onClose} aria-label="Close">
            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
              <line x1="1" y1="1" x2="12" y2="12"/><line x1="12" y1="1" x2="1" y2="12"/>
            </svg>
          </button>
        </div>

        <div className="pp__body">

          {/* User info */}
          <section className="pp__section">
            <div className="pp__user-row">
              {user.photoURL
                ? <img src={user.photoURL} alt="" className="pp__avatar" />
                : <span className="pp__avatar pp__avatar--initial">{initials}</span>
              }
              <div className="pp__user-info">
                <span className="pp__user-name">{user.displayName || 'Rider'}</span>
                <span className="pp__user-email">{user.email}</span>
              </div>
            </div>

            {!googleLinked && (
              <div className="pp__google-link">
                <p className="pp__connect-note">
                  Signed in with an email link? Link a Google account so you can sign in with one click instead.
                </p>
                <button
                  className="pp__btn pp__btn--outline"
                  onClick={handleLinkGoogle}
                  disabled={linkLoading}
                >
                  {linkLoading ? 'Linking…' : 'Link Google account'}
                </button>
              </div>
            )}
          </section>

          <div className="pp__divider" />

          {/* Calendar */}
          <section className="pp__section">
            <h3 className="pp__section-title">Calendar</h3>
            <ProfileCalendar />
          </section>

          <div className="pp__divider" />

          {/* Public profile */}
          <section className="pp__section">
            <h3 className="pp__section-title">Public Profile</h3>

            <div className="pp__follow-row">
              <div className="pp__follow-stats">
                <span><strong>{followerCount ?? '–'}</strong> followers</span>
                <span className="pp__follow-dot">·</span>
                <span><strong>{followingCount ?? '–'}</strong> following</span>
              </div>
              <button className="pp__btn pp__btn--outline" onClick={() => setShowPublic(true)}>View public profile</button>
            </div>

            <p className="pp__section-desc">Shown on the routes and tours you publish. Add your links so riders can find you.</p>

            <textarea
              className="pp__textarea"
              placeholder="Short bio (optional)"
              maxLength={240}
              rows={2}
              value={bio}
              onChange={e => setBio(e.target.value)}
            />

            <label className="pp__check-row">
              <input type="checkbox" checked={isBusiness} onChange={e => setIsBusiness(e.target.checked)} />
              <span>I’m a business / tour operator</span>
            </label>
            {isBusiness && (
              <div style={{ marginTop: 4 }}>
                {businessVerifiedStatus === 'verified' && (
                  <div className="pp__connect-connected"><CheckIcon /> Verified business</div>
                )}
                {businessVerifiedStatus === 'pending' && (
                  <div className="pp__connect-pending">In review</div>
                )}
                {businessVerifiedStatus === 'unverified' && (
                  <button type="button" className="pp__btn pp__btn--outline" onClick={() => setShowBusinessVerifyModal(true)}>
                    Apply to become a verified business
                  </button>
                )}
              </div>
            )}

            {isBusiness && businessVerifiedStatus === 'verified' && (
              <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 10 }}>
                <div>
                  <label className="pp__field-label" htmlFor="pp-business-logo">Business logo</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {businessLogoUrl
                      ? <img src={businessLogoUrl} alt="" style={{ width: 40, height: 40, borderRadius: '50%', objectFit: 'cover' }} />
                      : <span className="pp__gear-hint" style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--color-surface-2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{businessName?.[0]?.toUpperCase() ?? '?'}</span>
                    }
                    <input
                      id="pp-business-logo"
                      className="pp__input"
                      type="file"
                      accept="image/*"
                      disabled={logoUploading}
                      onChange={handleLogoChange}
                    />
                  </div>
                </div>

                <label className="pp__check-row">
                  <input type="checkbox" checked={useBusinessIdentity} onChange={toggleUseBusinessIdentity} />
                  <span>Use business details (name &amp; logo) for my public profile</span>
                </label>
                <p className="pp__connect-note" style={{ marginTop: 0 }}>
                  When on, your tours, lessons &amp; routes show {businessName || 'your business'} and its logo in Community, and a business card links to your website from their detail pages.
                </p>
              </div>
            )}

            <div className="pp__links">
              {links.map((l, i) => (
                <div className="pp__link-row" key={i}>
                  <select value={l.type} onChange={e => updateLink(i, { type: e.target.value })} className="pp__link-type">
                    {LINK_TYPES.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
                  </select>
                  <input
                    className="pp__input pp__link-url"
                    placeholder="https://…"
                    value={l.url}
                    onChange={e => updateLink(i, { url: e.target.value })}
                  />
                  <button className="pp__link-remove" onClick={() => removeLink(i)} aria-label="Remove">×</button>
                </div>
              ))}
              <button className="pp__btn pp__btn--outline pp__add-link" onClick={addLink}>+ Add link</button>
            </div>

            {/* Gearlist */}
            <div className="pp__gear-head">
              <span className="pp__gear-title">My gear</span>
              <span className="pp__gear-hint">Shown on your public profile</span>
            </div>
            <div className="pp__gear">
              {gear.map((g, i) => (
                <div className="pp__gear-row" key={i}>
                  <select value={g.category} onChange={e => updateGear(i, { category: e.target.value })} className="pp__gear-cat">
                    {GEAR_CATEGORIES.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
                  </select>
                  <div className="pp__gear-fields">
                    <input
                      className="pp__input"
                      placeholder="Model (e.g. Shoei NXR2)"
                      value={g.name}
                      onChange={e => updateGear(i, { name: e.target.value })}
                    />
                    <input
                      className="pp__input"
                      placeholder="Link (optional)"
                      value={g.url}
                      onChange={e => updateGear(i, { url: e.target.value })}
                    />
                  </div>
                  <button className="pp__link-remove" onClick={() => removeGear(i)} aria-label="Remove">×</button>
                </div>
              ))}
              {gear.length < MAX_GEAR && (
                <button className="pp__btn pp__btn--outline pp__add-link" onClick={addGear}>+ Add gear</button>
              )}
            </div>

            <button className="pp__btn pp__btn--primary" onClick={handleSaveProfile} disabled={profileSaving} style={{ alignSelf: 'flex-start' }}>
              {profileSaving ? 'Saving…' : 'Save profile'}
            </button>
          </section>

          <div className="pp__divider" />

          {/* Riding — license stage (any rider) */}
          <section className="pp__section">
            <h3 className="pp__section-title">Riding</h3>
            <p className="pp__section-desc">Helps coaches and other riders find the right fit for you.</p>

            <label className="pp__field-label" htmlFor="pp-license-stage">Your license stage</label>
            <select
              id="pp-license-stage"
              className="pp__input"
              value={licenseStage}
              onChange={e => setLicenseStage(e.target.value)}
            >
              <option value="">Prefer not to say</option>
              <option value="learner">Learner</option>
              <option value="provisional">Provisional / restricted</option>
              <option value="full">Full license</option>
            </select>

            <button
              className="pp__btn pp__btn--primary"
              onClick={handleSaveProfile}
              disabled={profileSaving}
              style={{ alignSelf: 'flex-start', marginTop: 14 }}
            >
              {profileSaving ? 'Saving…' : 'Save'}
            </button>
          </section>

          {showBusinessVerifyModal && (
            <BusinessApplicationModal
              uid={user.uid}
              initialData={{
                businessName, businessEmail, businessPhone, businessWebsite, businessLinks,
                businessServices, businessABN, businessInsuranceUrl, businessInsuranceFileName,
              }}
              onClose={() => setShowBusinessVerifyModal(false)}
              onSubmitted={(fields) => {
                setBusinessName(fields.businessName);
                setBusinessEmail(fields.businessEmail);
                setBusinessPhone(fields.businessPhone);
                setBusinessWebsite(fields.businessWebsite);
                setBusinessLinks(fields.businessLinks);
                setBusinessServices(fields.businessServices);
                setBusinessABN(fields.businessABN);
                setBusinessInsuranceUrl(fields.businessInsuranceUrl);
                setBusinessInsuranceFileName(fields.businessInsuranceFileName);
                setBusinessVerifiedStatus('pending');
                showToast('Application submitted');
              }}
            />
          )}

          <div className="pp__divider" />

          {/* Subscription */}
          <section className="pp__section">
            <h3 className="pp__section-title">Subscription</h3>

            <div className={`pp__sub-card${isSubscribed ? ' pp__sub-card--active' : ''}`}>
              <div className="pp__sub-status">
                {isSubscribed ? (
                  <>
                    <span className="pp__sub-badge pp__sub-badge--active">
                      <CheckIcon /> Pro
                    </span>
                    <span className="pp__sub-plan">{plan === 'yearly' ? 'Annual plan' : plan === 'monthly' ? 'Monthly plan' : 'Active'}</span>
                  </>
                ) : (
                  <span className="pp__sub-badge pp__sub-badge--free">Free plan</span>
                )}
              </div>

              {isSubscribed ? (
                <div className="pp__sub-perks">
                  <div className="pp__perk"><CheckIcon /> Motorcycle-tuned routing &amp; smarter route generation</div>
                  <div className="pp__perk"><CheckIcon /> Weather, fuel stops &amp; points of interest along your route</div>
                  <div className="pp__perk"><CheckIcon /> Elevation baked into every GPX export</div>
                  <div className="pp__perk"><CheckIcon /> Priority support</div>
                </div>
              ) : (
                <>
                  <div className="pp__sub-cta-text">
                    Publish tours, sell tickets, and unlock unlimited saved routes.
                  </div>
                  <div className="pp__plan-picker">
                    <button
                      className={`pp__plan-opt${selectedPlan === 'monthly' ? ' pp__plan-opt--active' : ''}`}
                      onClick={() => setSelectedPlan('monthly')}
                    >
                      <span className="pp__plan-name">Monthly</span>
                      <span className="pp__plan-price">${pricing.monthly.amount.toFixed(2)}<span>/mo</span></span>
                    </button>
                    <button
                      className={`pp__plan-opt${selectedPlan === 'yearly' ? ' pp__plan-opt--active' : ''}`}
                      onClick={() => setSelectedPlan('yearly')}
                    >
                      <span className="pp__plan-name">
                        Annual
                        <span className="pp__plan-save">Best value</span>
                      </span>
                      <span className="pp__plan-price">${pricing.yearly.amount.toFixed(2)}<span>/yr</span></span>
                    </button>
                  </div>
                  <button
                    className="pp__btn pp__btn--stripe"
                    onClick={handleUpgrade}
                    disabled={checkoutLoading}
                  >
                    {checkoutLoading
                      ? <><span className="pp__spinner pp__spinner--white" /> Opening Stripe…</>
                      : 'Start Pro'}
                  </button>
                </>
              )}
            </div>

            <div className="pp__sub-actions">
              {isSubscribed ? (
                <button
                  className="pp__btn pp__btn--outline"
                  onClick={handleManageBilling}
                  disabled={portalLoading}
                >
                  {portalLoading ? 'Opening…' : 'Manage Billing'}
                </button>
              ) : null}
              <button
                className="pp__btn pp__btn--ghost"
                onClick={handleSyncSubscription}
                disabled={syncLoading}
              >
                {syncLoading ? 'Syncing…' : 'Refresh status'}
              </button>
            </div>
          </section>

          <div className="pp__divider" />

          {/* Stripe Connect */}
          <section className="pp__section">
            <h3 className="pp__section-title">Email Notifications</h3>
            <p className="pp__section-desc">
              Booking confirmations and refunds are always emailed. These are optional:
            </p>
            <div className="pp__email-prefs">
              {[
                { key: 'follows',         label: 'New routes & tours from riders you follow' },
                { key: 'newRegistration', label: 'Someone registers for your tour' },
                { key: 'tourUpdates',     label: 'Updates to tours you’re registered for' },
                { key: 'tourReminder',    label: 'Reminder the day before a tour you’re registered for' },
              ].map(({ key, label }) => (
                <button key={key} type="button" className="pp__email-pref" onClick={() => toggleEmailPref(key)}>
                  <span className="pp__email-pref-label">{label}</span>
                  <span className={`route-option__toggle${emailPrefs[key] ? ' route-option__toggle--on' : ''}`}>
                    <span className="route-option__thumb" />
                  </span>
                </button>
              ))}
            </div>
          </section>

          {profile?.businessVerifiedStatus === 'verified' && (
            <>
              <div className="pp__divider" />
              <OrganizerPayouts
                connectStatus={connectStatus}
                connectLoading={connectLoading}
                connectError={connectError}
                onConnectStripe={handleConnectStripe}
              />
            </>
          )}

        </div>

        {/* Legal footer */}
        <div className="pp__legal">
          <button className="pp__legal-link" onClick={() => setLegalTab('terms')}>Terms</button>
          <span className="pp__legal-sep">·</span>
          <button className="pp__legal-link" onClick={() => setLegalTab('privacy')}>Privacy</button>
          <span className="pp__legal-sep">·</span>
          <button className="pp__legal-link" onClick={() => setLegalTab('refund')}>Refunds</button>
        </div>

        {toast && <div className="pp__toast">{toast}</div>}
      </aside>
    </div>,
    document.body
  );

  return <>
    {panel}
    {legalTab && <LegalModal initialTab={legalTab} onClose={() => setLegalTab(null)} />}
    {showPublic && user && (
      <UserProfilePanel
        uid={user.uid}
        fallbackName={user.displayName}
        fallbackPhoto={user.photoURL}
        zIndex={1600}
        onClose={() => setShowPublic(false)}
      />
    )}
  </>;
}
